import { Redacted, Schema } from "effect";
import { ContentIdentityText, Sha256Identity, CompatibilityDate } from "../../identity-input.ts";
import { JsonObject } from "../../canonical-json/model.ts";
import { TreeId } from "../../development-identifiers.ts";
import { StationPath } from "../../station-path.ts";
import { privateDependencyFailure } from "../../errors/private-cause.ts";

export const DependencyLayerKey = /* @__PURE__ */ (() => Schema.String.check(Schema.isPattern(/^dependency-layer:v1:sha256:[0-9a-f]{64}$/)).pipe(Schema.brand("DependencyLayerKey")))();
export type DependencyLayerKey = typeof DependencyLayerKey.Type;
export const BuildArtifactKey = /* @__PURE__ */ (() => Schema.String.check(Schema.isPattern(/^build-artifact:v1:sha256:[0-9a-f]{64}$/)).pipe(Schema.brand("BuildArtifactKey")))();
export type BuildArtifactKey = typeof BuildArtifactKey.Type;

export const DEPENDENCY_LAYER_KEY_SCHEMA_VERSION = 1 as const;
export const BUILD_ARTIFACT_KEY_SCHEMA_VERSION = 1 as const;

export const LockfileFingerprintInput = /* @__PURE__ */ (() => Schema.Struct({
  path: Schema.String.pipe(Schema.mutableKey),
  format: ContentIdentityText.pipe(Schema.mutableKey),
  digest: Sha256Identity.pipe(Schema.mutableKey),
}))();
export type LockfileFingerprintInput = typeof LockfileFingerprintInput.Type;

export const ToolchainIdentityInput = /* @__PURE__ */ (() => Schema.Struct({
  name: ContentIdentityText.pipe(Schema.mutableKey),
  version: ContentIdentityText.pipe(Schema.mutableKey),
  implementationDigest: Sha256Identity.pipe(Schema.mutableKey),
}))();
export type ToolchainIdentityInput = typeof ToolchainIdentityInput.Type;

export const RuntimeIdentityInput = /* @__PURE__ */ (() => Schema.Struct({
  ...ToolchainIdentityInput.fields,
  platform: ContentIdentityText.pipe(Schema.mutableKey),
  architecture: ContentIdentityText.pipe(Schema.mutableKey),
}))();
export type RuntimeIdentityInput = typeof RuntimeIdentityInput.Type;

export const DependencyLayerKeyInput = /* @__PURE__ */ (() => Schema.Struct({
  lockfiles: Schema.Array(LockfileFingerprintInput).pipe(Schema.mutableKey),
  registryPolicy: JsonObject.pipe(Schema.mutableKey),
  installer: ToolchainIdentityInput.pipe(Schema.mutableKey),
  runtime: RuntimeIdentityInput.pipe(Schema.mutableKey),
}))();
export type DependencyLayerKeyInput = typeof DependencyLayerKeyInput.Type;

export const LockfileFingerprint = /* @__PURE__ */ (() => Schema.Struct({
  path: StationPath,
  format: ContentIdentityText,
  digest: Sha256Identity,
}))();
export type LockfileFingerprint = typeof LockfileFingerprint.Type;

export const ToolchainIdentity = /* @__PURE__ */ (() => Schema.Struct({
  name: ContentIdentityText,
  version: ContentIdentityText,
  implementationDigest: Sha256Identity,
}))();
export type ToolchainIdentity = typeof ToolchainIdentity.Type;

export const RuntimeIdentity = /* @__PURE__ */ (() => Schema.Struct({
  ...ToolchainIdentity.fields,
  platform: ContentIdentityText,
  architecture: ContentIdentityText,
}))();
export type RuntimeIdentity = typeof RuntimeIdentity.Type;

export const DependencyLayerKeyManifest = /* @__PURE__ */ (() => Schema.Struct({
  schemaVersion: Schema.Literal(DEPENDENCY_LAYER_KEY_SCHEMA_VERSION),
  lockfiles: Schema.Array(LockfileFingerprint),
  registryPolicyDigest: Sha256Identity,
  installer: ToolchainIdentity,
  runtime: RuntimeIdentity,
}))();
export type DependencyLayerKeyManifest = typeof DependencyLayerKeyManifest.Type;

export const DependencyLayerKeyResult = /* @__PURE__ */ (() => Schema.Struct({
  key: DependencyLayerKey,
  manifest: DependencyLayerKeyManifest,
  canonicalJson: Schema.String,
}))();
export type DependencyLayerKeyResult = typeof DependencyLayerKeyResult.Type;

export const BundlerIdentityInput = ToolchainIdentityInput;
export type BundlerIdentityInput = typeof BundlerIdentityInput.Type;

export const CompatibilityInput = /* @__PURE__ */ (() => Schema.Struct({
  date: CompatibilityDate.pipe(Schema.mutableKey),
  flags: Schema.Array(ContentIdentityText).pipe(Schema.mutableKey),
}))();
export type CompatibilityInput = typeof CompatibilityInput.Type;

export const BindingSchemaInput = /* @__PURE__ */ (() => Schema.Struct({
  bindingName: ContentIdentityText.pipe(Schema.mutableKey),
  bindingType: ContentIdentityText.pipe(Schema.mutableKey),
  schema: JsonObject.pipe(Schema.mutableKey),
}))();
export type BindingSchemaInput = typeof BindingSchemaInput.Type;

export const BuildArtifactKeyInput = /* @__PURE__ */ (() => Schema.Struct({
  treeId: TreeId.pipe(Schema.mutableKey),
  dependencyLayerKey: DependencyLayerKey.pipe(Schema.mutableKey),
  bundler: BundlerIdentityInput.pipe(Schema.mutableKey),
  compatibility: CompatibilityInput.pipe(Schema.mutableKey),
  bindingSchema: Schema.Array(BindingSchemaInput).pipe(Schema.mutableKey),
  buildOptions: JsonObject.pipe(Schema.mutableKey),
}))();
export type BuildArtifactKeyInput = typeof BuildArtifactKeyInput.Type;

export const CompatibilityIdentity = /* @__PURE__ */ (() => Schema.Struct({
  date: CompatibilityDate,
  flags: Schema.Array(ContentIdentityText),
}))();
export type CompatibilityIdentity = typeof CompatibilityIdentity.Type;

export const BindingSchemaIdentity = /* @__PURE__ */ (() => Schema.Struct({
  bindingName: ContentIdentityText,
  bindingType: ContentIdentityText,
  schemaDigest: Sha256Identity,
}))();
export type BindingSchemaIdentity = typeof BindingSchemaIdentity.Type;

export const BuildArtifactKeyManifest = /* @__PURE__ */ (() => Schema.Struct({
  schemaVersion: Schema.Literal(BUILD_ARTIFACT_KEY_SCHEMA_VERSION),
  treeId: TreeId,
  dependencyLayerKey: DependencyLayerKey,
  bundler: ToolchainIdentity,
  compatibility: CompatibilityIdentity,
  bindingSchema: Schema.Array(BindingSchemaIdentity),
  buildOptionsDigest: Sha256Identity,
}))();
export type BuildArtifactKeyManifest = typeof BuildArtifactKeyManifest.Type;

export const BuildArtifactKeyResult = /* @__PURE__ */ (() => Schema.Struct({
  key: BuildArtifactKey,
  manifest: BuildArtifactKeyManifest,
  canonicalJson: Schema.String,
}))();
export type BuildArtifactKeyResult = typeof BuildArtifactKeyResult.Type;

export const BuildIdentityError = /* @__PURE__ */ (() => {
  class BuildIdentityError extends privateDependencyFailure<BuildIdentityError>()("BuildIdentityError", "build identity") {}
  return BuildIdentityError;
})();
export type BuildIdentityError = InstanceType<typeof BuildIdentityError>;
export const buildIdentityError = (cause: unknown) => new BuildIdentityError({ cause: Redacted.make(cause) });
